import { useEffect, useRef, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AccessDeniedPage } from '@/components/pages/WorkspaceStatusPage';
import { recordAllowedPath } from '@/utils/routeAccess';

type RouteAccessGateProps = {
  denied: boolean;
  message?: string;
  /** Kept for callers; landing path is resolved inside AccessDeniedPage. */
  fallbackPath?: string;
  /** When true, skip recording path (unused with inline access-denied UX). */
  silent?: boolean;
  canAccessPath?: (pathname: string) => boolean;
  extraRevertCandidates?: string[];
  children?: ReactNode;
};

/** Blocks outlet and shows an in-layout access denied page — sidebar and header stay visible. */
export function RouteAccessGate({
  denied,
  message,
  children,
}: RouteAccessGateProps) {
  const location = useLocation();
  const lastAllowedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!denied) {
      recordAllowedPath(location.pathname, location.search);
      lastAllowedRef.current = `${location.pathname}${location.search}`;
    }
  }, [denied, location.pathname, location.search]);

  if (denied) {
    return <AccessDeniedPage message={message} />;
  }

  return children ?? <Outlet />;
}
